/**
 * Function for setting focus when navigate from keyboard. Support up and down navigation.
 * @param {HTMLElement} node
 * @param {Object} options - options object
 * @param {string} options.selector - selector for items
 * @param {HTMLElement} options.observableElement - element with changeable value, which trigger update function
 * @param {boolean} options.isFocusable - flag to set focus
 */

import { KEYBOARD_EVENT_KEYS } from "@/constants/key-codes";

export const setFocusOnKeyDown = (
    node: HTMLElement,
    options: {
        selector: string;
        observableElement?: HTMLElement;
        isFocusable?: boolean;
    }
) => {
    if (!options.isFocusable) return;

    let elements: HTMLElement[];
    let elementsLength: number;
    let startIndex: number;
    let endIndex: number;
    let nextIndex: number;

    const handleKeyDown = (event: KeyboardEvent) => {
        const currentIndex = elements.findIndex(
            (element) => element === event.target
        );
        nextIndex = currentIndex;

        if (event.code === KEYBOARD_EVENT_KEYS.ArrowDown) {
            // Prevents page scroll while user is navigating
            event.preventDefault();
            moveNext();
        }

        if (event.code === KEYBOARD_EVENT_KEYS.ArrowUp) {
            // Prevents page scroll while user is navigating
            event.preventDefault();
            movePrev();
        }
        elements[nextIndex].focus();
    };

    const moveNext = () => {
        nextIndex += 1;
        if (nextIndex > endIndex) {
            nextIndex = startIndex;
        }
    };

    const movePrev = () => {
        nextIndex -= 1;
        if (nextIndex < startIndex) {
            nextIndex = endIndex;
        }
    };

    const update = () => {
        elements = [
            ...node.querySelectorAll<HTMLElement>(`${options.selector}`),
        ];
        elementsLength = elements.length;
        startIndex = 0;
        endIndex = elementsLength - 1;
        nextIndex = 0;
    };

    if (!options.observableElement) update();
    node.addEventListener("keydown", handleKeyDown);

    return {
        update,
        destroy() {
            node.removeEventListener("keydown", handleKeyDown);
        },
    };
};
